/**
 * Trade Outcomes Component
 * Displays best/worst symbols, best/worst trades, and scratch trade count in a DataTable
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { SymbolStatisticsResponse } from "../../../../types/trade";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { StatisticsCardHeadline, STATS_MUTED_TEXT_CLASS } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { statisticsShadcnCardClassName } from "./statisticsShadcnCardShell";
import { DataTable } from "../../../../components/ui/data-table";
import { DashboardWidgetLoadingCard } from "../../Dashboard/components/DashboardWidgetLoadingCard";

interface TradeOutcomesProps {
  symbolStats: SymbolStatisticsResponse | null;
  statistics: {
    largestWin: number;
    largestLoss: number;
    closedTrades: number;
    winningTrades: number;
    losingTrades: number;
    totalReturnPercent: number;
    averageReturnPercent: number;
  };
  backendStats?: {
    best_trade?: {
      trade_id: string | null;
      symbol: string | null;
      trade_type: "buy" | "sell" | null;
      net_pnl: number;
      entry_date: string | null;
      exit_date: string | null;
    } | null;
    worst_trade?: {
      trade_id: string | null;
      symbol: string | null;
      trade_type: "buy" | "sell" | null;
      net_pnl: number;
      entry_date: string | null;
      exit_date: string | null;
    } | null;
    most_traded_symbol?: string | null;
    most_traded_symbol_count?: number | null;
  } | null;
  loading?: boolean;
}

interface OutcomeRow {
  metric: string;
  result: string;
  value: string;
  numericValue?: number;
}

const TradeOutcomes: React.FC<TradeOutcomesProps> = ({
  symbolStats,
  statistics,
  backendStats,
  loading = false,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const { best, worst } = useMemo(() => {
    if (!symbolStats || !symbolStats.symbols || symbolStats.symbols.length === 0) {
      return { best: undefined, worst: undefined };
    }
    const sorted = [...symbolStats.symbols].sort((a, b) => b.total_pnl - a.total_pnl);
    return {
      best: sorted[0] || undefined,
      worst: sorted[sorted.length - 1] || undefined,
    };
  }, [symbolStats]);

  const { bestTrade, worstTrade } = useMemo(() => {
    const bestT = backendStats?.best_trade;
    const worstT = backendStats?.worst_trade;

    return {
      bestTrade: bestT
        ? { netPnL: bestT.net_pnl, symbol: bestT.symbol || "N/A", type: (bestT.trade_type || "buy") as "buy" | "sell" }
        : statistics.largestWin > 0
          ? { netPnL: statistics.largestWin, symbol: "N/A", type: "buy" as const }
          : null,
      worstTrade: worstT
        ? { netPnL: worstT.net_pnl, symbol: worstT.symbol || "N/A", type: (worstT.trade_type || "sell") as "buy" | "sell" }
        : statistics.largestLoss < 0
          ? { netPnL: statistics.largestLoss, symbol: "N/A", type: "sell" as const }
          : null,
    };
  }, [statistics, backendStats]);

  const scratchTrades = statistics.closedTrades - statistics.winningTrades - statistics.losingTrades;

  const tableData: OutcomeRow[] = useMemo(() => [
    {
      metric: "Best Symbol",
      result: best ? `${best.symbol} (${best.total_trades} trades)` : "N/A",
      value: best ? formatMoney(best.total_pnl) : "N/A",
      numericValue: best ? best.total_pnl : 0,
    },
    {
      metric: "Worst Symbol",
      result: worst ? `${worst.symbol} (${worst.total_trades} trades)` : "N/A",
      value: worst ? formatMoney(worst.total_pnl) : "N/A",
      numericValue: worst ? worst.total_pnl : 0,
    },
    {
      metric: "Best Trade",
      result: bestTrade ? `${bestTrade.symbol} - ${bestTrade.type.toUpperCase()}` : "N/A",
      value: bestTrade ? formatMoney(bestTrade.netPnL) : "N/A",
      numericValue: bestTrade ? bestTrade.netPnL : 0,
    },
    {
      metric: "Worst Trade",
      result: worstTrade ? `${worstTrade.symbol} - ${worstTrade.type.toUpperCase()}` : "N/A",
      value: worstTrade ? formatMoney(worstTrade.netPnL) : "N/A",
      numericValue: worstTrade ? worstTrade.netPnL : 0,
    },
    {
      metric: "Scratch Trades",
      result: "Breakeven trades",
      value: String(scratchTrades),
    },
    {
      metric: "Most Traded Symbol",
      result: backendStats?.most_traded_symbol
        ? `${backendStats.most_traded_symbol_count ?? 0} trades`
        : "",
      value: backendStats?.most_traded_symbol ?? "N/A",
    },
    {
      metric: "Total Return",
      result: "Overall % return",
      value: `${statistics.totalReturnPercent.toFixed(2)}%`,
    },
    {
      metric: "Avg Return",
      result: "Per trade % return",
      value: `${statistics.averageReturnPercent.toFixed(2)}%`,
    },
  ], [best, worst, bestTrade, worstTrade, scratchTrades, backendStats, statistics, formatMoney]);

  const columns: ColumnDef<OutcomeRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={STATS_MUTED_TEXT_CLASS}>
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "result",
        header: "Result",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={STATS_MUTED_TEXT_CLASS}>
            {row.getValue("result")}
          </span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => {
          const metric = row.original.metric;
          const numericValue = row.original.numericValue;
          const isTradeValue = metric === "Best Trade" || metric === "Worst Trade";
          const color = isTradeValue && numericValue !== undefined
            ? numericValue >= 0 ? "#22c55e" : "#ef4444"
            : undefined;
          return (
            <span
              className={STATS_MUTED_TEXT_CLASS}
              style={color ? { color } : undefined}
              data-trade-log-pnl={color ? "" : undefined}
            >
              {row.getValue("value")}
            </span>
          );
        },
      },
    ],
    [],
  );

  if (loading) {
    return (
      <DashboardWidgetLoadingCard
        cardType="performanceBreakdown"
        variant="table"
        className="h-full min-h-0 flex-1"
      />
    );
  }

  return (
    <div
      data-statistics-shadcn-card
      data-statistics-trade-outcomes=""
      className={cn(
        statisticsShadcnCardClassName,
        "h-full min-h-0 flex-1 p-4",
      )}
    >
      <div className="mb-4">
        <StatisticsCardHeadline>Trade Outcomes</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={tableData}
        showPagination={false}
        emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
      />
    </div>
  );
};

export default TradeOutcomes;
